/**
 * Tenant-app addon registry bootstrap.
 *
 * Wave 1 hardcodes vendor addons into the registry at app boot. Later
 * phases will swap this for a manifest-driven dynamic loader (AI-authored
 * addons, Phase 9). Until then, every "vendor" addon gets registered by
 * pairing its `code` bundle with an inlined manifest object.
 *
 * We inline the manifest here (rather than fetching the YAML at runtime) so
 * Vite never has to ship a YAML loader to the browser. Source of truth for
 * the declaration remains each addon's `manifest.yaml` — addon package
 * tests parse and validate the YAML directly, catching any drift.
 */

import {
  type AddonCode,
  type AddonManifest,
  AddonRegistry,
  parseAddonManifest,
} from '@sazeo/addon-runtime';
import type { ClaudeScope, ClaudeTool } from '@sazeo/claude';
import { type ComponentType, Suspense, createElement, lazy } from 'react';
import { z } from 'zod';

type AddonModule = {
  code: AddonCode;
  claudeTools?: ClaudeTool<unknown, unknown>[];
} & Record<string, unknown>;

function createLazyAddonComponent<TKey extends string>(
  load: () => Promise<AddonModule>,
  exportName: TKey,
): ComponentType<Record<string, unknown>> {
  const LazyComponent = lazy(async () => {
    const mod = await load();
    return {
      default: mod[exportName] as ComponentType<Record<string, unknown>>,
    };
  });

  function WrappedLazyAddonComponent(props: Record<string, unknown>) {
    return createElement(
      Suspense,
      {
        fallback: createElement('div', {
          className: 'h-24 animate-pulse rounded-[var(--radius)] bg-[var(--color-surface)]',
        }),
      },
      createElement(LazyComponent, props),
    );
  }

  WrappedLazyAddonComponent.displayName = `LazyAddon(${exportName})`;
  return WrappedLazyAddonComponent;
}

function createLazyAddonHandler<TKey extends string>(
  load: () => Promise<AddonModule>,
  exportName: TKey,
): (...args: unknown[]) => Promise<unknown> {
  return async (...args: unknown[]) => {
    const mod = await load();
    const handler = mod[exportName] as ((...inner: unknown[]) => Promise<unknown>) | undefined;
    if (!handler) {
      throw new Error(`[addons] missing handler export "${exportName}"`);
    }
    return handler(...args);
  };
}

function createLazyClaudeTool(
  load: () => Promise<AddonModule>,
  name: string,
  description: string,
  requiredScopes: readonly ClaudeScope[],
  approvalRequired: boolean,
): ClaudeTool<unknown, unknown> {
  return {
    name,
    description,
    requiredScopes,
    approvalRequired,
    inputSchema: z.any(),
    run: async (args, ctx) => {
      const mod = await load();
      const tool = mod.claudeTools?.find((entry) => entry.name === name);
      if (!tool) {
        throw new Error(`[addons] missing Claude tool export "${name}"`);
      }
      return tool.run(args, ctx);
    },
  };
}

/**
 * Inlined mirror of `addons/attendance/manifest.yaml`. If the YAML changes,
 * update both places and the attendance addon's manifest test will fail if
 * they diverge.
 */
const attendanceManifestRaw = {
  schema_version: 1,
  id: 'attendance',
  name: 'Attendance',
  description: 'Clock-in / clock-out tracking with shift management',
  category: 'hr',
  version: '1.0.0',
  vendor: 'sazeo',
  authoring: 'human',
  pricing: {
    model: 'per_seat',
    price_usd: 3,
    unit: 'month',
    trial_days: 14,
  },
  permissions: [
    { scope: 'attendance.read' },
    { scope: 'attendance.write' },
    { scope: 'users.read' },
    { scope: 'notifications.send' },
  ],
  extension_points: {
    menu_entry: {
      icon: 'clock',
      route: '/attendance',
      label_key: 'addons.attendance.menu_label',
      order: 50,
      requires_role: ['member', 'manager', 'admin'],
    },
    dashboard_widgets: [
      {
        id: 'today_clocked_in',
        component: 'TodayClockedInWidget',
        default_size: 'medium',
        default_enabled: true,
      },
    ],
    entity_tabs: [
      {
        entity: 'user',
        tab_key: 'addons.attendance.tab_title',
        component: 'UserAttendanceTab',
      },
    ],
    inline_actions: [
      {
        entity: 'user',
        action: 'clock_in_now',
        label_key: 'addons.attendance.action_clock_in',
        handler: 'clockInNow',
      },
    ],
  },
  api: {
    endpoints: [
      {
        method: 'GET',
        path: '/entries',
        handler: 'listEntries',
        scopes: ['attendance.read'],
      },
      {
        method: 'POST',
        path: '/clock-in',
        handler: 'clockInNow',
        scopes: ['attendance.write'],
      },
    ],
    webhooks: ['attendance.clocked_in', 'attendance.clocked_out'],
  },
  tables: [
    {
      name: 'attendance_entries',
      migration: 'migrations/0001_attendance_entries.sql',
    },
  ],
  translations: {
    i18n_namespace: 'addons.attendance',
    source_lang: 'en',
  },
  claude_tools: [
    {
      name: 'get_attendance_today',
      description: 'Get who has clocked in today',
      read_scope: 'attendance.read',
    },
    {
      name: 'clock_in_user',
      description: 'Clock in a user (requires approval)',
      write_scope: 'attendance.write',
      approval_required: true,
    },
  ],
} as const;

/** Inlined mirror of `addons/crm-leads/manifest.yaml`. */
const crmLeadsManifestRaw = {
  schema_version: 1,
  id: 'crm-leads',
  name: 'CRM Leads',
  description: 'Lead pipeline management with stages, activities, and owner assignments',
  category: 'sales',
  version: '1.0.0',
  vendor: 'sazeo',
  authoring: 'human',
  pricing: {
    model: 'per_seat',
    price_usd: 5,
    unit: 'month',
    trial_days: 14,
  },
  permissions: [
    { scope: 'crm.read' },
    { scope: 'crm.write' },
    { scope: 'users.read' },
    { scope: 'notifications.send' },
  ],
  extension_points: {
    menu_entry: {
      icon: 'briefcase',
      route: '/addons/crm-leads',
      label_key: 'addons.crm-leads.menu_label',
      order: 60,
      requires_role: ['member', 'manager', 'admin'],
    },
    dashboard_widgets: [
      {
        id: 'open_leads',
        component: 'OpenLeadsWidget',
        default_size: 'medium',
        default_enabled: true,
      },
      {
        id: 'win_rate',
        component: 'WinRateWidget',
        default_size: 'small',
        default_enabled: true,
      },
    ],
    entity_tabs: [
      {
        entity: 'user',
        tab_key: 'addons.crm-leads.tab_owned_leads',
        component: 'LeadOwnedByUserTab',
      },
    ],
    inline_actions: [
      {
        entity: 'user',
        action: 'assign_lead',
        label_key: 'addons.crm-leads.inline_assign_lead',
        handler: 'assignLead',
      },
    ],
  },
  api: {
    endpoints: [
      { method: 'GET', path: '/leads', handler: 'listLeads', scopes: ['crm.read'] },
      { method: 'POST', path: '/leads', handler: 'createLead', scopes: ['crm.write'] },
      { method: 'PATCH', path: '/leads/:id', handler: 'updateLead', scopes: ['crm.write'] },
      { method: 'DELETE', path: '/leads/:id', handler: 'deleteLead', scopes: ['crm.write'] },
      {
        method: 'POST',
        path: '/leads/:id/transition',
        handler: 'transitionLead',
        scopes: ['crm.write'],
      },
      {
        method: 'GET',
        path: '/leads/:id/activities',
        handler: 'listLeadActivities',
        scopes: ['crm.read'],
      },
      {
        method: 'POST',
        path: '/leads/:id/activities',
        handler: 'addLeadActivity',
        scopes: ['crm.write'],
      },
    ],
    webhooks: ['crm.lead.created', 'crm.lead.updated', 'crm.lead.won', 'crm.lead.lost'],
  },
  tables: [
    { name: 'crm_pipelines', migration: 'migrations/0001_crm_and_invoicing.sql' },
    { name: 'crm_stages', migration: 'migrations/0001_crm_and_invoicing.sql' },
    { name: 'crm_leads', migration: 'migrations/0001_crm_and_invoicing.sql' },
    { name: 'crm_lead_activities', migration: 'migrations/0001_crm_and_invoicing.sql' },
  ],
  translations: {
    i18n_namespace: 'addons.crm-leads',
    source_lang: 'en',
  },
  claude_tools: [
    {
      name: 'list_open_leads',
      description: 'List open (not won/lost) leads for the current tenant',
      read_scope: 'crm.read',
    },
    {
      name: 'summarize_lead_pipeline',
      description: 'Summarise the lead pipeline — counts by stage + aggregate value',
      read_scope: 'crm.read',
    },
    {
      name: 'create_lead',
      description: 'Create a new lead (requires approval)',
      write_scope: 'crm.write',
      approval_required: true,
    },
  ],
} as const;

/** Inlined mirror of `addons/vacations/src/index.ts` manifest. */
const vacationsManifestRaw = {
  schema_version: 1,
  id: 'vacations',
  name: 'Vacations',
  description: 'Leave requests and balance tracking',
  category: 'hr',
  version: '1.0.0',
  vendor: 'sazeo',
  authoring: 'human',
  pricing: {
    model: 'per_seat',
    price_usd: 2,
    unit: 'month',
    trial_days: 14,
  },
  permissions: [
    { scope: 'leave_requests.read' },
    { scope: 'leave_requests.write' },
    { scope: 'users.read' },
  ],
  extension_points: {
    menu_entry: {
      icon: 'umbrella',
      route: '/vacations',
      label_key: 'addons.vacations.title',
      order: 55,
      requires_role: ['member', 'manager', 'admin'],
    },
  },
  api: {
    endpoints: [
      { method: 'GET', path: '/types', handler: 'listLeaveTypes', scopes: ['leave_requests.read'] },
      {
        method: 'GET',
        path: '/requests',
        handler: 'listLeaveRequests',
        scopes: ['leave_requests.read'],
      },
      {
        method: 'GET',
        path: '/balance',
        handler: 'getLeaveBalance',
        scopes: ['leave_requests.read'],
      },
      {
        method: 'POST',
        path: '/requests',
        handler: 'createLeaveRequest',
        scopes: ['leave_requests.write'],
      },
      {
        method: 'PATCH',
        path: '/requests/:id/review',
        handler: 'reviewLeaveRequest',
        scopes: ['leave_requests.write'],
      },
      {
        method: 'PATCH',
        path: '/requests/:id/cancel',
        handler: 'cancelLeaveRequest',
        scopes: ['leave_requests.write'],
      },
    ],
    webhooks: [
      'vacations.request.created',
      'vacations.request.approved',
      'vacations.request.rejected',
    ],
  },
  tables: [
    { name: 'leave_types', migration: 'migrations/0030_vacations.sql' },
    { name: 'leave_balances', migration: 'migrations/0030_vacations.sql' },
    { name: 'leave_requests', migration: 'migrations/0030_vacations.sql' },
  ],
  translations: {
    i18n_namespace: 'addons.vacations',
    source_lang: 'en',
  },
  claude_tools: [
    {
      name: 'check_leave_balance',
      description: 'Check leave balance for a user',
      read_scope: 'leave_requests.read',
    },
    {
      name: 'request_leave',
      description: 'Submit a leave request (requires approval)',
      write_scope: 'leave_requests.write',
      approval_required: true,
    },
  ],
} as const;

/** Inlined mirror of `addons/invoicing/manifest.yaml`. */
const invoicingManifestRaw = {
  schema_version: 1,
  id: 'invoicing',
  name: 'Invoicing',
  description: 'Issue invoices, track customers, and collect payments',
  category: 'finance',
  version: '1.0.0',
  vendor: 'sazeo',
  authoring: 'human',
  pricing: {
    model: 'per_seat',
    price_usd: 4,
    unit: 'month',
    trial_days: 14,
  },
  permissions: [
    { scope: 'invoicing.read' },
    { scope: 'invoicing.write' },
    { scope: 'users.read' },
    { scope: 'notifications.send' },
  ],
  extension_points: {
    menu_entry: {
      icon: 'receipt',
      route: '/addons/invoicing',
      label_key: 'addons.invoicing.menu_label',
      order: 70,
      requires_role: ['member', 'manager', 'admin'],
    },
    dashboard_widgets: [
      {
        id: 'overdue_invoices',
        component: 'OverdueInvoicesWidget',
        default_size: 'medium',
        default_enabled: true,
      },
      {
        id: 'month_revenue',
        component: 'MonthRevenueWidget',
        default_size: 'medium',
        default_enabled: true,
      },
    ],
    // No entity_tabs / inline_actions — proves runtime tolerates absence (§2.2).
  },
  api: {
    endpoints: [
      { method: 'GET', path: '/customers', handler: 'listCustomers', scopes: ['invoicing.read'] },
      {
        method: 'POST',
        path: '/customers',
        handler: 'createCustomer',
        scopes: ['invoicing.write'],
      },
      { method: 'GET', path: '/customers/:id', handler: 'getCustomer', scopes: ['invoicing.read'] },
      {
        method: 'PATCH',
        path: '/customers/:id',
        handler: 'updateCustomer',
        scopes: ['invoicing.write'],
      },
      {
        method: 'DELETE',
        path: '/customers/:id',
        handler: 'deleteCustomer',
        scopes: ['invoicing.write'],
      },
      { method: 'GET', path: '/invoices', handler: 'listInvoices', scopes: ['invoicing.read'] },
      { method: 'POST', path: '/invoices', handler: 'createInvoice', scopes: ['invoicing.write'] },
      { method: 'GET', path: '/invoices/:id', handler: 'getInvoice', scopes: ['invoicing.read'] },
      {
        method: 'PATCH',
        path: '/invoices/:id',
        handler: 'updateInvoice',
        scopes: ['invoicing.write'],
      },
      {
        method: 'DELETE',
        path: '/invoices/:id',
        handler: 'deleteInvoice',
        scopes: ['invoicing.write'],
      },
      {
        method: 'POST',
        path: '/invoices/:id/send',
        handler: 'sendInvoice',
        scopes: ['invoicing.write'],
      },
      {
        method: 'POST',
        path: '/invoices/:id/mark-paid',
        handler: 'markInvoicePaid',
        scopes: ['invoicing.write'],
      },
    ],
    webhooks: [
      'invoicing.invoice.created',
      'invoicing.invoice.sent',
      'invoicing.invoice.paid',
      'invoicing.invoice.overdue',
    ],
  },
  tables: [
    { name: 'inv_customers', migration: 'migrations/0001_crm_and_invoicing.sql' },
    { name: 'inv_invoices', migration: 'migrations/0001_crm_and_invoicing.sql' },
    { name: 'inv_invoice_lines', migration: 'migrations/0001_crm_and_invoicing.sql' },
  ],
  translations: {
    i18n_namespace: 'addons.invoicing',
    source_lang: 'en',
  },
  claude_tools: [
    {
      name: 'list_overdue_invoices',
      description: 'List invoices past due date that are not paid or cancelled',
      read_scope: 'invoicing.read',
    },
    {
      name: 'compute_month_revenue',
      description: 'Compute revenue from paid invoices for a given month',
      read_scope: 'invoicing.read',
    },
    {
      name: 'mark_invoice_paid',
      description: 'Mark an invoice as paid (requires approval)',
      write_scope: 'invoicing.write',
      approval_required: true,
    },
  ],
} as const;

/** Inlined manifest for the asset-management addon. */
const assetManagementManifestRaw = {
  schema_version: 1,
  id: 'asset-management',
  name: 'Asset Management',
  description: 'Track company-owned laptops, phones, and equipment.',
  category: 'operations',
  version: '1.0.0',
  vendor: 'sazeo',
  authoring: 'human',
  pricing: {
    model: 'per_seat',
    price_usd: 2,
    unit: 'month',
    trial_days: 14,
  },
  permissions: [{ scope: 'assets.read' }, { scope: 'assets.write' }, { scope: 'users.read' }],
  extension_points: {
    menu_entry: {
      icon: 'package',
      route: '/addons/assets',
      label_key: 'addons.assets.menu_label',
      order: 80,
      requires_role: ['member', 'manager', 'admin'],
    },
  },
  api: { endpoints: [], webhooks: [] },
  tables: [{ name: 'company_assets', migration: 'migrations/0067_asset_management.sql' }],
  translations: {
    i18n_namespace: 'addons.assets',
    source_lang: 'en',
  },
} as const;

/** Inlined manifest for the training addon. */
const trainingManifestRaw = {
  schema_version: 1,
  id: 'training',
  name: 'Training & Certifications',
  description: 'Manage employee training courses and track certifications.',
  category: 'hr',
  version: '1.0.0',
  vendor: 'sazeo',
  authoring: 'human',
  pricing: {
    model: 'per_seat',
    price_usd: 2,
    unit: 'month',
    trial_days: 14,
  },
  permissions: [{ scope: 'training.read' }, { scope: 'training.write' }, { scope: 'users.read' }],
  extension_points: {
    menu_entry: {
      icon: 'book-open',
      route: '/addons/training',
      label_key: 'addons.training.menu_label',
      order: 85,
      requires_role: ['member', 'manager', 'admin'],
    },
  },
  api: { endpoints: [], webhooks: [] },
  tables: [
    { name: 'training_courses', migration: 'migrations/0068_training.sql' },
    { name: 'training_enrollments', migration: 'migrations/0068_training.sql' },
  ],
  translations: {
    i18n_namespace: 'addons.training',
    source_lang: 'en',
  },
} as const;

function parseManifestOrThrow(raw: unknown, source: string): AddonManifest {
  const result = parseAddonManifest(raw);
  if (result instanceof z.ZodError) {
    throw new Error(
      `[addons] failed to parse ${source}: ${JSON.stringify(result.issues, null, 2)}`,
    );
  }
  return result;
}

const attendanceManifest = parseManifestOrThrow(
  attendanceManifestRaw,
  'addons/attendance/manifest.yaml',
);
const crmLeadsManifest = parseManifestOrThrow(
  crmLeadsManifestRaw,
  'addons/crm-leads/manifest.yaml',
);
const invoicingManifest = parseManifestOrThrow(
  invoicingManifestRaw,
  'addons/invoicing/manifest.yaml',
);
const vacationsManifest = parseManifestOrThrow(vacationsManifestRaw, 'addons/vacations/manifest');
const assetManagementManifest = parseManifestOrThrow(
  assetManagementManifestRaw,
  'addons/asset-management/manifest',
);
const trainingManifest = parseManifestOrThrow(trainingManifestRaw, 'addons/training/manifest');
/** Inlined manifest for the helpdesk addon. */
const helpdeskManifestRaw = {
  schema_version: 1,
  id: 'helpdesk',
  name: 'Helpdesk',
  description: 'Internal IT/HR support tickets and request management.',
  category: 'operations',
  version: '1.0.0',
  vendor: 'sazeo',
  authoring: 'human',
  pricing: {
    model: 'per_seat',
    price_usd: 2,
    unit: 'month',
    trial_days: 14,
  },
  permissions: [{ scope: 'helpdesk.read' }, { scope: 'helpdesk.write' }, { scope: 'users.read' }],
  extension_points: {
    menu_entry: {
      icon: 'life-buoy',
      route: '/addons/helpdesk',
      label_key: 'addons.helpdesk.menu_label',
      order: 90,
      requires_role: ['member', 'manager', 'admin'],
    },
  },
  api: { endpoints: [], webhooks: [] },
  tables: [
    { name: 'helpdesk_tickets', migration: 'migrations/0047_helpdesk.sql' },
    { name: 'helpdesk_replies', migration: 'migrations/0047_helpdesk.sql' },
  ],
  translations: {
    i18n_namespace: 'addons.helpdesk',
    source_lang: 'en',
  },
} as const;

/** Inlined manifest for the projects addon. */
const projectsManifestRaw = {
  schema_version: 1,
  id: 'projects',
  name: 'Projects',
  description: 'Project management with tasks and team membership.',
  category: 'operations',
  version: '1.0.0',
  vendor: 'sazeo',
  authoring: 'human',
  pricing: {
    model: 'per_seat',
    price_usd: 2,
    unit: 'month',
    trial_days: 14,
  },
  permissions: [{ scope: 'projects.read' }, { scope: 'projects.write' }, { scope: 'users.read' }],
  extension_points: {
    menu_entry: {
      icon: 'folder-kanban',
      route: '/addons/projects',
      label_key: 'addons.projects.menu_label',
      order: 92,
      requires_role: ['member', 'manager', 'admin'],
    },
  },
  api: { endpoints: [], webhooks: [] },
  tables: [
    { name: 'projects', migration: 'migrations/0046_projects.sql' },
    { name: 'project_members', migration: 'migrations/0046_projects.sql' },
    { name: 'project_tasks', migration: 'migrations/0046_projects.sql' },
  ],
  translations: {
    i18n_namespace: 'addons.projects',
    source_lang: 'en',
  },
} as const;

const helpdeskManifest = parseManifestOrThrow(helpdeskManifestRaw, 'addons/helpdesk/manifest');
const projectsManifest = parseManifestOrThrow(projectsManifestRaw, 'addons/projects/manifest');

const loadAttendanceModule = () => import('@sazeo/addon-attendance') as Promise<AddonModule>;
const loadCrmLeadsModule = () => import('@sazeo/addon-crm-leads') as Promise<AddonModule>;
const loadInvoicingModule = () => import('@sazeo/addon-invoicing') as Promise<AddonModule>;
const loadVacationsModule = () => import('@sazeo/addon-vacations') as Promise<AddonModule>;

const attendanceCode: AddonCode = {
  components: {
    TodayClockedInWidget: createLazyAddonComponent(loadAttendanceModule, 'TodayClockedInWidget'),
    UserAttendanceTab: createLazyAddonComponent(loadAttendanceModule, 'UserAttendanceTab'),
    AttendancePage: createLazyAddonComponent(loadAttendanceModule, 'AttendancePage'),
  },
  handlers: {
    clockInNow: createLazyAddonHandler(loadAttendanceModule, 'clockInNow'),
    clockIn: createLazyAddonHandler(loadAttendanceModule, 'clockIn'),
    clockOut: createLazyAddonHandler(loadAttendanceModule, 'clockOut'),
    listEntries: createLazyAddonHandler(loadAttendanceModule, 'listEntries'),
  },
  claudeTools: [
    createLazyClaudeTool(
      loadAttendanceModule,
      'get_attendance_today',
      'Get who has clocked in today',
      ['read.people'],
      false,
    ),
    createLazyClaudeTool(
      loadAttendanceModule,
      'clock_in_user',
      'Clock in a user (requires approval)',
      ['write.tasks'],
      true,
    ),
  ],
};

const crmLeadsCode: AddonCode = {
  components: {
    CRMLeadsPage: createLazyAddonComponent(loadCrmLeadsModule, 'CRMLeadsPage'),
    LeadsPage: createLazyAddonComponent(loadCrmLeadsModule, 'LeadsPage'),
    LeadDetailPage: createLazyAddonComponent(loadCrmLeadsModule, 'LeadDetailPage'),
    LeadDetailDrawer: createLazyAddonComponent(loadCrmLeadsModule, 'LeadDetailDrawer'),
    LeadOwnedByUserTab: createLazyAddonComponent(loadCrmLeadsModule, 'LeadOwnedByUserTab'),
    OpenLeadsWidget: createLazyAddonComponent(loadCrmLeadsModule, 'OpenLeadsWidget'),
    WinRateWidget: createLazyAddonComponent(loadCrmLeadsModule, 'WinRateWidget'),
    DashboardWinRateWidget: createLazyAddonComponent(loadCrmLeadsModule, 'DashboardWinRateWidget'),
  },
  handlers: {
    listLeads: createLazyAddonHandler(loadCrmLeadsModule, 'listLeads'),
    getLead: createLazyAddonHandler(loadCrmLeadsModule, 'getLead'),
    createLead: createLazyAddonHandler(loadCrmLeadsModule, 'createLead'),
    updateLead: createLazyAddonHandler(loadCrmLeadsModule, 'updateLead'),
    deleteLead: createLazyAddonHandler(loadCrmLeadsModule, 'deleteLead'),
    transitionLead: createLazyAddonHandler(loadCrmLeadsModule, 'transitionLead'),
    listLeadActivities: createLazyAddonHandler(loadCrmLeadsModule, 'listLeadActivities'),
    addLeadActivity: createLazyAddonHandler(loadCrmLeadsModule, 'addLeadActivity'),
    assignLead: createLazyAddonHandler(loadCrmLeadsModule, 'assignLead'),
  },
  claudeTools: [
    createLazyClaudeTool(
      loadCrmLeadsModule,
      'list_open_leads',
      'List open (not won/lost) leads for the current tenant',
      ['read.analytics'],
      false,
    ),
    createLazyClaudeTool(
      loadCrmLeadsModule,
      'summarize_lead_pipeline',
      'Summarise the lead pipeline — counts by stage + aggregate value',
      ['read.analytics'],
      false,
    ),
    createLazyClaudeTool(
      loadCrmLeadsModule,
      'create_lead',
      'Create a new lead (requires approval)',
      ['write.tasks'],
      true,
    ),
  ],
};

const invoicingCode: AddonCode = {
  components: {
    InvoicesPage: createLazyAddonComponent(loadInvoicingModule, 'InvoicesPage'),
    InvoiceDetailPage: createLazyAddonComponent(loadInvoicingModule, 'InvoiceDetailPage'),
    CustomersPage: createLazyAddonComponent(loadInvoicingModule, 'CustomersPage'),
    CustomerDetailPage: createLazyAddonComponent(loadInvoicingModule, 'CustomerDetailPage'),
    OverdueInvoicesWidget: createLazyAddonComponent(loadInvoicingModule, 'OverdueInvoicesWidget'),
    MonthRevenueWidget: createLazyAddonComponent(loadInvoicingModule, 'MonthRevenueWidget'),
  },
  handlers: {
    listCustomers: createLazyAddonHandler(loadInvoicingModule, 'listCustomers'),
    getCustomer: createLazyAddonHandler(loadInvoicingModule, 'getCustomer'),
    createCustomer: createLazyAddonHandler(loadInvoicingModule, 'createCustomer'),
    updateCustomer: createLazyAddonHandler(loadInvoicingModule, 'updateCustomer'),
    deleteCustomer: createLazyAddonHandler(loadInvoicingModule, 'deleteCustomer'),
    listInvoices: createLazyAddonHandler(loadInvoicingModule, 'listInvoices'),
    getInvoice: createLazyAddonHandler(loadInvoicingModule, 'getInvoice'),
    createInvoice: createLazyAddonHandler(loadInvoicingModule, 'createInvoice'),
    updateInvoice: createLazyAddonHandler(loadInvoicingModule, 'updateInvoice'),
    deleteInvoice: createLazyAddonHandler(loadInvoicingModule, 'deleteInvoice'),
    sendInvoice: createLazyAddonHandler(loadInvoicingModule, 'sendInvoice'),
    markInvoicePaid: createLazyAddonHandler(loadInvoicingModule, 'markInvoicePaid'),
    addInvoiceLine: createLazyAddonHandler(loadInvoicingModule, 'addInvoiceLine'),
    listInvoiceLines: createLazyAddonHandler(loadInvoicingModule, 'listInvoiceLines'),
  },
  claudeTools: [
    createLazyClaudeTool(
      loadInvoicingModule,
      'list_overdue_invoices',
      'List invoices past due date that are not paid or cancelled',
      ['read.analytics'],
      false,
    ),
    createLazyClaudeTool(
      loadInvoicingModule,
      'compute_month_revenue',
      'Compute revenue from paid invoices for a given month',
      ['read.analytics'],
      false,
    ),
    createLazyClaudeTool(
      loadInvoicingModule,
      'mark_invoice_paid',
      'Mark an invoice as paid (requires approval)',
      ['write.tasks'],
      true,
    ),
  ],
};

const vacationsCode: AddonCode = {
  components: {
    LeaveBalanceCard: createLazyAddonComponent(loadVacationsModule, 'LeaveBalanceCard'),
    LeaveRequestsTable: createLazyAddonComponent(loadVacationsModule, 'LeaveRequestsTable'),
  },
  handlers: {
    listLeaveTypes: createLazyAddonHandler(loadVacationsModule, 'listLeaveTypes'),
    listLeaveRequests: createLazyAddonHandler(loadVacationsModule, 'listLeaveRequests'),
    getLeaveBalance: createLazyAddonHandler(loadVacationsModule, 'getLeaveBalance'),
    createLeaveRequest: createLazyAddonHandler(loadVacationsModule, 'createLeaveRequest'),
    reviewLeaveRequest: createLazyAddonHandler(loadVacationsModule, 'reviewLeaveRequest'),
    cancelLeaveRequest: createLazyAddonHandler(loadVacationsModule, 'cancelLeaveRequest'),
  },
  claudeTools: [
    createLazyClaudeTool(
      loadVacationsModule,
      'check_leave_balance',
      'Check leave balance for a user',
      ['read.people'],
      false,
    ),
    createLazyClaudeTool(
      loadVacationsModule,
      'request_leave',
      'Submit a leave request (requires approval)',
      ['write.tasks'],
      true,
    ),
  ],
};

const assetManagementCode: AddonCode = {
  components: {},
  handlers: {},
};

const trainingCode: AddonCode = {
  components: {},
  handlers: {},
};

/** Shared process-wide registry instance. Recreated on HMR in dev. */
export const addonRegistry = new AddonRegistry();

/**
 * Register every vendor addon into the module-local registry. Idempotent
 * guard — Vite HMR can re-execute this file, and `register()` throws on
 * duplicates.
 */
let bootstrapped = false;
export function bootstrapAddonRegistry(): AddonRegistry {
  if (bootstrapped) return addonRegistry;
  bootstrapped = true;

  addonRegistry.register(attendanceManifest, attendanceCode);

  addonRegistry.register(crmLeadsManifest, crmLeadsCode);

  addonRegistry.register(invoicingManifest, invoicingCode);

  addonRegistry.register(vacationsManifest, vacationsCode);

  addonRegistry.register(assetManagementManifest, assetManagementCode);

  addonRegistry.register(trainingManifest, trainingCode);

  addonRegistry.register(helpdeskManifest, { components: {}, handlers: {} });

  addonRegistry.register(projectsManifest, { components: {}, handlers: {} });

  return addonRegistry;
}
